import React, { useState, useEffect } from 'react';
import { Table, Card, Btn, SectionHeader, Badge, StatCard, NeedProject, ProgressBar, RNLLoader } from '../components/UI';
import { fmt, fmtNum, fmtDate, statusColor, exportCSV } from '../utils';
import * as api from '../api';
import { useParams } from 'react-router-dom';

// ─── BOQ ANALYTICAL SUMMARY ──────────────────
function BOQAnalyticsTab({ projectId }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!projectId) return;
    setLoading(true);
    api.getAnalyticalSummary(projectId)
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [projectId]);

  if (loading) return <RNLLoader title="Loading Analytics..." subtitle="Please wait" />;

  if (!data) return <div style={{ color: 'var(--text-muted)', padding: '40px 0', textAlign: 'center' }}>No analytics data yet. Add BOQ items and costs first.</div>;

  return (
    <div>
      {/* Summary KPIs */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(155px,1fr))', gap: 12, marginBottom: 20 }}>
        <StatCard label="Total Budgeted"  value={fmt(data.total_budgeted)}   color="accent" />
        <StatCard label="Total Committed" value={fmt(data.total_committed)}  color="amber" />
        <StatCard label="Total Actual"    value={fmt(data.total_actual)}     color="red" />
        <StatCard label="Total Certified" value={fmt(data.total_certified)}  color="green" />
        <StatCard label="Overall Variance"value={fmt(data.overall_variance)} color={parseFloat(data.overall_variance) >= 0 ? 'green' : 'red'} />
        <StatCard label="Over Budget Items" value={data.over_budget_count || 0} color={data.over_budget_count > 0 ? 'red' : 'green'} />
      </div>

      {/* DIVIDER */}
      <hr style={{ border: 'none', borderTop: '3px solid #94a3b8', margin: '0 0 32px 0' }} />

      {/* Line items table */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 10 }}>
        <Btn variant="ghost" onClick={() => exportCSV(data.line_items || [], 'boq-analytics')}>⬇ CSV</Btn>
      </div>
      <Card>
        <Table cols={[
          { key: 'boq_code',          label: 'Code',      render: v => <span style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--accent)' }}>{v}</span> },
          { key: 'description',       label: 'Description',render: v => <span style={{ maxWidth: 200, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v}</span> },
          { key: 'budgeted_amount',   label: 'Budget',    render: v => <span style={{ fontFamily: 'var(--mono)' }}>{fmt(v)}</span> },
          { key: 'committed_amount',  label: 'Committed', render: v => <span style={{ fontFamily: 'var(--mono)', color: 'var(--amber)' }}>{fmt(v)}</span> },
          { key: 'actual_total',      label: 'Actual',    render: v => <span style={{ fontFamily: 'var(--mono)', color: 'var(--red)' }}>{fmt(v)}</span> },
          { key: 'certified_amount',  label: 'Certified', render: v => <span style={{ fontFamily: 'var(--mono)', color: 'var(--green)' }}>{fmt(v)}</span> },
          { key: 'variance_percentage', label: 'Var %',   render: (v, row) => (
            <span style={{ fontFamily: 'var(--mono)', fontWeight: 700, color: row.is_over_budget ? 'var(--red)' : 'var(--green)' }}>
              {parseFloat(v || 0).toFixed(1)}%
            </span>
          )},
          { key: 'is_over_budget',    label: '',          render: v => v ? <Badge color="red">⚠ Over</Badge> : null },
        ]} rows={data.line_items || []} />
      </Card>
    </div>
  );
}

// ─── COST SUMMARY ────────────────────────────
function CostSummaryTab({ projectId }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!projectId) return;
    setLoading(true);
    api.getCostSummary(projectId)
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [projectId]);

  if (loading) return <RNLLoader title="Loading Summary..." subtitle="Please wait" />;

  if (!data) return <div style={{ color: 'var(--text-muted)', padding: '40px 0', textAlign: 'center' }}>No cost summary available.</div>;

  const fields = [
    { label: 'Contract Value',        value: fmt(data.contract_value) },
    { label: 'Original Budget',       value: fmt(data.original_budget) },
    { label: 'Contingency Budget',    value: fmt(data.contingency_budget) },
    { label: 'Contingency Used',      value: fmt(data.contingency_used),          color: parseFloat(data.contingency_used) > 0 ? 'amber' : undefined },
    { label: 'Total Committed',       value: fmt(data.total_committed),            color: 'amber' },
    { label: 'Total Actual Spent',    value: fmt(data.total_actual_spent),         color: 'red' },
    { label: 'Total Variations',      value: fmt(data.total_variations),           color: 'purple' },
    { label: 'Projected Final Cost',  value: fmt(data.projected_final_cost) },
    { label: 'Budget Variance',       value: fmt(data.budget_variance),            color: parseFloat(data.budget_variance) >= 0 ? 'green' : 'red' },
    { label: 'Profit Margin %',       value: `${parseFloat(data.profit_margin_pct || 0).toFixed(2)}%`, color: parseFloat(data.profit_margin_pct) >= 0 ? 'green' : 'red' },
    { label: 'VAT Rate',              value: `${data.vat_rate || 0}%` },
    { label: 'WHT Rate',              value: `${data.wht_rate || 0}%` },
    { label: 'Contract Type',         value: data.contract_type || '—' },
    { label: 'Currency',              value: data.currency || '—' },
  ];

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(155px,1fr))', gap: 12, marginBottom: 20 }}>
        <StatCard label="Contract Value"   value={fmt(data.contract_value)}       color="accent" />
        <StatCard label="Committed"        value={fmt(data.total_committed)}      color="amber" />
        <StatCard label="Actual Spent"     value={fmt(data.total_actual_spent)}   color="red" />
        <StatCard label="Profit Margin"    value={`${parseFloat(data.profit_margin_pct || 0).toFixed(1)}%`} color={parseFloat(data.profit_margin_pct) >= 0 ? 'green' : 'red'} />
      </div>

      {/* DIVIDER */}
      <hr style={{ border: 'none', borderTop: '3px solid #94a3b8', margin: '0 0 32px 0' }} />

      {/* Utilisation bars */}
      <div className="grid-cols-2" style={{ gap: 16, marginBottom: 20 }}>
        <Card style={{ padding: 20 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 14 }}>Budget Utilisation</div>
          {[
            { label: 'Committed',    value: data.total_committed,    max: data.original_budget, color: 'amber' },
            { label: 'Actual Spent', value: data.total_actual_spent, max: data.original_budget, color: 'red' },
            { label: 'Contingency',  value: data.contingency_used,   max: data.contingency_budget, color: 'purple' },
          ].map(r => (
            <div key={r.label} style={{ marginBottom: 14 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 5 }}>
                <span style={{ color: 'var(--text-secondary)' }}>{r.label}</span>
                <span style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--text-muted)' }}>{fmt(r.value)} / {fmt(r.max)}</span>
              </div>
              <ProgressBar value={parseFloat(r.value)} max={parseFloat(r.max)} color={r.color} />
            </div>
          ))}
        </Card>

        <Card style={{ padding: 20 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 14 }}>All Figures</div>
          {fields.map(f => (
            <div key={f.label} style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 0', borderBottom: '1px solid var(--border)' }}>
              <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{f.label}</span>
              <span style={{ fontFamily: 'var(--mono)', fontSize: 13, fontWeight: 600, color: f.color ? `var(--${f.color})` : 'var(--text-primary)' }}>{f.value}</span>
            </div>
          ))}
        </Card>
      </div>
    </div>
  );
}

// ─── PROCUREMENT PLAN ────────────────────────
function ProcurementPlanTab({ projectId }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!projectId) return;
    setLoading(true);
    api.getProcurementPlan(projectId)
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [projectId]);

  if (loading) return <RNLLoader title="Loading Plan..." subtitle="Please wait" />;

  return (
    <div>
      {data && (
        <div className="grid-cols-3" style={{ gap: 12, marginBottom: 20 }}>
          <StatCard label="Total Orders"     value={data.total_orders || 0} />
          <StatCard label="Committed Value"  value={fmt(data.total_committed_value)} color="amber" />
          <StatCard label="Overdue Orders"   value={data.overdue_count || 0} color={data.overdue_count > 0 ? 'red' : 'green'} />
        </div>
      )}
      
      {/* DIVIDER */}
      <hr style={{ border: 'none', borderTop: '3px solid #94a3b8', margin: '0 0 32px 0' }} />

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 10 }}>
        <Btn variant="ghost" onClick={() => exportCSV(data?.purchase_orders || [], 'procurement-plan')}>⬇ CSV</Btn>
      </div>
      <Card>
        <Table loading={loading} cols={[
          { key: 'po_number',               label: 'PO #',      render: v => <span style={{ fontFamily: 'var(--mono)', color: 'var(--accent)' }}>{v}</span> },
          { key: 'supplier_name',           label: 'Supplier' },
          { key: 'material_name',           label: 'Material' },
          { key: 'order_date',              label: 'Order Date',    render: v => fmtDate(v) },
          { key: 'expected_delivery_date',  label: 'Exp. Delivery', render: v => fmtDate(v) },
          { key: 'lead_time_days',          label: 'Lead Days' },
          { key: 'total_amount',            label: 'Value',     render: v => <span style={{ fontFamily: 'var(--mono)' }}>{fmt(v)}</span> },
          { key: 'status',                  label: 'Status',    render: v => <Badge color={statusColor(v)}>{v}</Badge> },
          { key: 'is_overdue',              label: '',          render: (v, row) => v ? <Badge color="red">⚠ {row.days_overdue}d late</Badge> : null },
        ]} rows={data?.purchase_orders || []} />
      </Card>
    </div>
  );
}

// ─── LABOUR ANALYTICS ────────────────────────
function LabourAnalyticsTab({ projectId }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!projectId) return;
    setLoading(true);
    api.getLabourAnalytics(projectId)
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [projectId]);

  if (loading) return <RNLLoader title="Loading Analytics..." subtitle="Please wait" />;

  if (!data) return <div style={{ color: 'var(--text-muted)', padding: '40px 0', textAlign: 'center' }}>No labour analytics yet.</div>;

  return (
    <div>
      <div className="grid-cols-3" style={{ gap: 12, marginBottom: 20 }}>
        <StatCard label="Total Entries"  value={data.total_entries  || 0} />
        <StatCard label="Total Hours"    value={fmtNum(data.total_hours)} color="cyan" />
        <StatCard label="Total Cost"     value={fmt(data.total_cost)}     color="accent" />
      </div>
      
      {/* DIVIDER */}
      <hr style={{ border: 'none', borderTop: '3px solid #94a3b8', margin: '0 0 32px 0' }} />

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 10 }}>
        <Btn variant="ghost" onClick={() => exportCSV(data.trades || [], 'labour-analytics')}>⬇ CSV</Btn>
      </div>
      <Card>
        <Table cols={[
          { key: 'trade',       label: 'Trade' },
          { key: 'entry_count', label: 'Entries' },
          { key: 'total_hours', label: 'Total Hours', render: v => <span style={{ fontFamily: 'var(--mono)' }}>{fmtNum(v)}</span> },
          { key: 'total_cost',  label: 'Total Cost',  render: v => <span style={{ fontFamily: 'var(--mono)', fontWeight: 700, color: 'var(--accent)' }}>{fmt(v)}</span> },
          { key: 'total_cost',  label: '% of Total',  render: (v) => {
            const pct = data.total_cost > 0 ? (parseFloat(v) / parseFloat(data.total_cost)) * 100 : 0;
            return <ProgressBar value={pct} max={100} color="cyan" />;
          }},
        ]} rows={data.trades || []} />
      </Card>
    </div>
  );
}

// ─── MAIN ─────────────────────────────────────
export default function AnalyticsPage() {
  const { projectId, tab } = useParams();

  if (!projectId) return <NeedProject />;
  return (
    <div className="fade-in">
      <SectionHeader title="Analytics & Reports" subtitle={tab === 'cost' ? 'Cost Summary' : tab === 'boq' ? 'BOQ Analysis' : tab === 'labour' ? 'Labour Analytics' : 'Procurement Plan'} />
      {tab === 'cost'        && <CostSummaryTab      projectId={projectId} />}
      {tab === 'boq'         && <BOQAnalyticsTab     projectId={projectId} />}
      {tab === 'labour'      && <LabourAnalyticsTab  projectId={projectId} />}
      {tab === 'procurement' && <ProcurementPlanTab  projectId={projectId} />}
    </div>
  );
}
